import { AddSharp, Edit } from '@mui/icons-material'
import { Container, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, ButtonGroup, IconButton, TextField, Skeleton, Fab, MenuItem } from '@mui/material'
import AppMenu from '../../../components/AppMenu'
import './AccountsPage.scss'
import { useEffect, useState } from 'react'
import { CreateAccountRequest, MinimalAccount, MinimalStore } from '../../../apis/ersys-data'
import { showError } from '../../../features/errors/Errors'
import { setIsLoading } from '../../../features/menus/AppMenu'
import { useDispatch } from 'react-redux'
import { useErsysDataApi } from '../../../components/ErsysDataApiProvider'
import { Link, useNavigate } from 'react-router-dom'
import AccountCreationModal from './AccountCreationModal'
import { useDebounce } from '../../../components/useDebounce'
import { usePermissions } from '../../../components/usePermissions'
import { useFirebase } from '../../../components/FirebaseProvider'

function AccountsPage() {
    const ersysDataApiServices = useErsysDataApi()
    const [accounts, setAccounts] = useState<MinimalAccount[]>([])
    const [stores, setStores] = useState<MinimalStore[]>([])
    const [selectedStore, setSelectedStore] = useState<number>(0)
    const [storesLookup, setStoresLookup] = useState<Record<number, string>>({})
    const [filter, setFilter] = useState<string>("")
    const [loading, setLoading] = useState<boolean>(false)
    const [showCreation, setShowCreation] = useState<boolean>(false)
    const dispatch = useDispatch()
    const { hasPermissions } = usePermissions()
    const navigate = useNavigate();
    const debouncedFilter: string = useDebounce(filter, 250)
    const { isReady } = useFirebase()

    useEffect(() => {
        dispatch(setIsLoading(loading))
    }, [loading, dispatch])

    useEffect(() => {
        if(!isReady) {
            return
        }
        const fetch = async () => {
            try {
                var resp = await ersysDataApiServices.accountsService.accountsList(debouncedFilter, selectedStore)
                setAccounts(resp.data.accounts)
            }
            catch (ex: any) {
                dispatch(
                    showError({
                        title: "Unexpected Error occurred",
                        message:
                            "An unexpected error occurred while getting account data. If problem persists please contact IT.",
                    }),
                )
                console.log(ex)
            }
        }
        fetch()
    }, [dispatch, setAccounts, ersysDataApiServices.accountsService, debouncedFilter, selectedStore, isReady])

    useEffect(() => {
        if(!isReady) {
            return
        }
        const fetch = async () => {
            try {
                var resp = await ersysDataApiServices.storesService.storesList()
                const set: Record<number, string> = {}
                for (const store of resp.data.stores) {
                    set[store.id] = store.fullName
                }
                resp.data.stores.sort((a, b) => (a.fullName > b.fullName) ? 1 : ((b.fullName > a.fullName) ? -1 : 0))
                setStores(resp.data.stores)
                setStoresLookup(set)
            }
            catch (ex: any) {
                dispatch(
                    showError({
                        title: "Unexpected Error occurred",
                        message:
                            "An unexpected error occurred while getting store data. If problem persists please contact IT.",
                    }),
                )
                console.log(ex)
            }
        }
        fetch()
    }, [dispatch, setAccounts, ersysDataApiServices.storesService, isReady])

    const lookupStoreName = (id: number): string => {
        if (id in storesLookup) {
            return storesLookup[id]
        }
        return ""
    }

    const truncate = (str: string, len: number): string => {
        return str.length > len ? str.substring(0, len - 3) + "..." : str;
    }

    const showSkeleton = () => {
        // stores shouldn't be empty, and if filter is empty then the filter shouldn't be.
        return accounts.length < 1 && debouncedFilter === ''
    }

    const onCreate = async (req: CreateAccountRequest) => {
        setLoading(true)
        try {
            setShowCreation(false)
            var resp = await ersysDataApiServices.accountsService.accountsCreate(req)
            navigate(`/accounts/${resp.data.id}`)
        }
        catch (ex: any) {
            dispatch(
                showError({
                    title: "Unexpected Error occurred",
                    message:
                        "An unexpected error occurred while getting account data. If problem persists please contact IT.",
                }),
            )
            console.log(ex)
        }
        finally {
            setLoading(false)
        }
    }

    if (!isReady) {
        return <AppMenu title="Accounts">
            <Skeleton variant="rectangular" height={800} sx={{width: '100%'}}/>
        </AppMenu>
    }

    return (
        <AppMenu title="Accounts">
            {hasPermissions("AP:AP+") ?
                <Tooltip title="Add Account">
                    <Fab
                        onClick={() => setShowCreation(true)}
                        color="primary"
                        aria-label="add"
                        sx={{ position: 'absolute', bottom: 25, right: 25 }}>
                        <AddSharp />
                    </Fab>
                </Tooltip>
                :
                <></>
            }
            <AccountCreationModal
                open={showCreation}
                onClose={() => setShowCreation(false)}
                onCreate={onCreate}
                stores={stores}
            />
            <Container maxWidth="md">

                <TextField
                    select
                    value={selectedStore}
                    onChange={e => setSelectedStore(parseInt(e.target.value, 10))}
                    size="small"
                >
                    <MenuItem key={0} value={0}>Filter By Store</MenuItem>
                    {stores.map(s => <MenuItem key={s.id} value={s.id}>{s.fullName}</MenuItem>)}
                </TextField>
                <TableContainer sx={{ minWidth: 650, maxHeight: 550 }} component={Paper}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Store</TableCell>
                                <TableCell align="right">
                                    <TextField
                                        size="small"
                                        value={filter}
                                        placeholder='Search'
                                        onChange={(e) => setFilter(e.target.value)} />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {showSkeleton() ? [...Array(6)].map((_, i) => (
                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }} key={`skele-${i}`}>
                                    <TableCell><Skeleton variant='rectangular' width={40} height={20} /></TableCell>
                                    <TableCell><Skeleton variant='rectangular' width={300} height={20} /></TableCell>
                                    <TableCell align="right">
                                        <ButtonGroup>
                                            <Skeleton variant='circular' width={30} height={30} />
                                        </ButtonGroup>
                                    </TableCell>
                                </TableRow>
                            )) :
                                accounts.map((account) => (
                                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }} key={account.id}>
                                        <TableCell>{account.id}</TableCell>
                                        <TableCell>{truncate(`${account.first} ${account.last}`, 100)}</TableCell>
                                        <TableCell>{truncate(lookupStoreName(account.storeId), 100)}</TableCell>
                                        <TableCell align="right">
                                            <ButtonGroup>
                                                <Tooltip title="Edit">
                                                    <Link to={`/accounts/${encodeURIComponent(account.id)}`}>
                                                        <IconButton>
                                                            <Edit></Edit>
                                                        </IconButton>
                                                    </Link>
                                                </Tooltip>
                                            </ButtonGroup>
                                        </TableCell>
                                    </TableRow>
                                ))
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Container>
        </AppMenu>
    )
}

export default AccountsPage